// src/components/Cockpit/WorkflowMiniDag.tsx
// ============================================
// WorkflowMiniDag - 工作流 DAG 微缩图（v2 视觉升级版）
//
// 视觉：
//   - 复用 @xyflow/react，极简模式
//   - 节点 36x24px，圆角矩形 + 状态色填充
//   - 节点边发光（运行中状态）
//   - 边带数据流动动画
//   - 顶部光带 + 玻璃拟态
// ============================================

import { useMemo } from "react";
import { motion } from "framer-motion";
import {
  ReactFlow,
  Background,
  BackgroundVariant,
  ReactFlowProvider,
  type Node,
  type Edge,
  type NodeProps,
} from "@xyflow/react";
import "@xyflow/react/dist/style.css";
import { StepStatus } from "../../runtime/WorkflowRuntime/types";
import type { WorkflowExecution } from "../../runtime/WorkflowRuntime/types";

// ============================================
// 节点状态色（更鲜明的霓虹色）
// ============================================
const STEP_COLOR: Record<StepStatus, string> = {
  [StepStatus.Pending]: "#64748B",
  [StepStatus.Running]: "#38BDF8",
  [StepStatus.Completed]: "#34D399",
  [StepStatus.Failed]: "#F87171",
  [StepStatus.Skipped]: "#94A3B8",
  [StepStatus.WaitingApproval]: "#FBBF24",
  [StepStatus.Canceled]: "#475569",
};

// ============================================
// 自定义节点（36x24 圆角矩形 + 状态色）
// ============================================
type MiniNodeData = {
  status: StepStatus;
  label: string;
};

function MiniNode({ data }: NodeProps<Node<MiniNodeData>>) {
  const color = STEP_COLOR[data.status];
  const isRunning = data.status === StepStatus.Running;
  const isCompleted = data.status === StepStatus.Completed;
  const isFailed = data.status === StepStatus.Failed;

  return (
    <div
      className="relative rounded border"
      style={{
        width: 36,
        height: 24,
        borderColor: color,
        background: isCompleted
          ? `${color}30`
          : isFailed
          ? `${color}40`
          : isRunning
          ? `${color}25`
          : `${color}10`,
        boxShadow: isRunning
          ? `0 0 12px ${color}, 0 0 4px ${color} inset`
          : isFailed
          ? `0 0 8px ${color}80`
          : `0 0 4px ${color}40`,
      }}
      title={`${data.label} · ${data.status}`}
    >
      {isRunning && (
        <motion.div
          className="absolute inset-0 rounded border"
          style={{ borderColor: color, background: color, opacity: 0.3 }}
          animate={{ opacity: [0.15, 0.5, 0.15] }}
          transition={{ duration: 1.2, repeat: Infinity, ease: "easeInOut" }}
        />
      )}
      {/* 中心点 */}
      <div
        className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-1 h-1 rounded-full"
        style={{ background: color, boxShadow: `0 0 4px ${color}` }}
      />
    </div>
  );
}

const nodeTypes = { mini: MiniNode };

// ============================================
// 由 execution 派生 nodes / edges
// ============================================
function buildGraph(execution: WorkflowExecution): { nodes: Node[]; edges: Edge[] } {
  const steps = execution.stepResults;
  if (steps.length === 0) {
    return { nodes: [], edges: [] };
  }

  // 水平布局：每两个节点之间间隔 70px
  const gapX = 70;
  const startX = 20;
  const y = 60;

  const nodes: Node[] = steps.map((step, idx) => ({
    id: step.stepId,
    type: "mini",
    position: { x: startX + idx * gapX, y },
    data: { status: step.status, label: step.stepName } satisfies MiniNodeData,
    draggable: false,
    selectable: false,
    connectable: false,
  }));

  const edges: Edge[] = steps.slice(1).map((step, idx) => {
    const prevStatus = steps[idx].status;
    const isCompleted = prevStatus === StepStatus.Completed;
    return {
      id: `e-${steps[idx].stepId}-${step.stepId}`,
      source: steps[idx].stepId,
      target: step.stepId,
      animated: isCompleted,
      style: {
        stroke: isCompleted ? "#34D399" : "#3B82F6",
        strokeWidth: 1.8,
      },
    };
  });

  return { nodes, edges };
}

// ============================================
// 内部 ReactFlow 组件
// ============================================
function DagCanvas({ execution }: { execution: WorkflowExecution }) {
  const { nodes, edges } = useMemo(() => buildGraph(execution), [execution]);

  if (nodes.length === 0) {
    return (
      <div className="flex items-center justify-center h-full text-[12px] text-gray-500 font-mono">
        暂无执行步骤
      </div>
    );
  }

  return (
    <ReactFlow
      nodes={nodes}
      edges={edges}
      nodeTypes={nodeTypes}
      fitView
      fitViewOptions={{ padding: 0.25 }}
      panOnDrag={false}
      zoomOnScroll={false}
      zoomOnPinch={false}
      zoomOnDoubleClick={false}
      nodesDraggable={false}
      nodesConnectable={false}
      elementsSelectable={false}
      proOptions={{ hideAttribution: true }}
      className="bg-transparent"
    >
      <Background
        variant={BackgroundVariant.Dots}
        gap={14}
        size={1}
        color="#1E293B"
      />
    </ReactFlow>
  );
}

// ============================================
// WorkflowMiniDag 容器
// ============================================
interface WorkflowMiniDagProps {
  execution: WorkflowExecution | null;
}

export default function WorkflowMiniDag({ execution }: WorkflowMiniDagProps) {
  const workflowName = execution?.workflowName ?? "—";
  const status = execution?.status ?? "—";
  const completedSteps = execution?.stepResults.filter(
    (s) => s.status === StepStatus.Completed
  ).length ?? 0;
  const totalSteps = execution?.stepResults.length ?? 0;
  const progressPct = totalSteps === 0 ? 0 : (completedSteps / totalSteps) * 100;

  return (
    <div className="relative rounded-xl border border-white/[0.06] bg-white/[0.02] backdrop-blur-md p-4 h-full flex flex-col overflow-hidden">
      {/* 顶部光带 */}
      <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-purple-500/40 to-transparent" />

      {/* 头部 */}
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-2">
          <div className="w-1 h-4 bg-gradient-to-b from-purple-400 to-purple-600 rounded-full" />
          <h3 className="text-sm font-medium text-white">Workflow DAG</h3>
        </div>
        <div className="flex items-center gap-2">
          {/* 进度条 */}
          <div className="w-12 h-1 bg-white/5 rounded-full overflow-hidden">
            <motion.div
              className="h-full bg-gradient-to-r from-purple-400 to-blue-500"
              initial={{ width: 0 }}
              animate={{ width: `${progressPct}%` }}
              transition={{ duration: 0.5 }}
            />
          </div>
          <span className="text-[11px] font-mono text-gray-400">
            <span className="text-emerald-400">{completedSteps}</span>
            <span className="text-gray-600">/</span>
            <span>{totalSteps}</span>
          </span>
        </div>
      </div>

      {/* 工作流名称 */}
      <div className="text-[10px] font-mono text-gray-500 mb-2 truncate">
        <span className="text-gray-400">{workflowName}</span>
        <span className="text-gray-600"> · </span>
        <span className="text-gray-500">{status}</span>
      </div>

      {/* DAG 画布 */}
      <div className="flex-1 min-h-0 rounded-md overflow-hidden">
        {execution ? (
          <ReactFlowProvider>
            <DagCanvas execution={execution} />
          </ReactFlowProvider>
        ) : (
          <div className="flex items-center justify-center h-full text-[12px] text-gray-500 font-mono">
            暂无工作流执行
          </div>
        )}
      </div>
    </div>
  );
}
